import React, { useMemo } from "react";
import { Field } from "redux-form";

import { ReduxControls } from "rhombus-react";

import { ASSET_TYPES } from "utils/constants";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { GOOGLE_DEMAND_GEN_CAROUSEL_CONSTRAINTS } from "social/validators/google";
import AssetSelect from "social/AssetSelect";

import "./GoogleDemandGenMainContent.css";

const {
	IMAGE_ACCEPT,
	MAX_LENGTH_HEADLINE,
	MAX_LENGTH_DESCRIPTION,
	MAX_LENGTH_LANDING_PAGE,
	MAX_LENGTH_BUSINESS_NAME,
} = GOOGLE_DEMAND_GEN_CAROUSEL_CONSTRAINTS;

export const GOOGLE_DEMAND_GEN_CAROUSEL_MAIN_FIELDS = {
	creativeName: {
		name: "name",
		label: "Creative Name",
		className: "full-width cypress-creative-name",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
		required: true,
	},
	headline: {
		name: "headline",
		label: "Headline",
		className: "full-width cypress-headline",
		placeholder: "Enter Headline...",
		tooltipVersion: 2,
		tip: {
			description: `The headline is the first line of your ad and can be up to 40 characters.<br />
			The length of the rendered headline will depend on the site it appears on.<br />
			If it's shortened, it will end with an ellipsis.`,
			place: "right",
			html: true,
		},
		component: ReduxControls.Input,
		charMax: MAX_LENGTH_HEADLINE,
		showCharCount: true,
		required: true,
	},
	description: {
		name: "description",
		label: "Description",
		placeholder: "Enter Description...",
		component: ReduxControls.Input,
		className: "full-width",
		tooltipVersion: 2,
		tip: {
			place: "right",
			description: `The description adds to the headline and provides additional context or details.<br />
			It can be up to 90 characters, and may appear after the headline.<br />

			The length of the rendered description will depend on the site it appears on.<br />
			If it's shortened, it will end with an ellipsis. The description doesn't show in all sizes and formats.`,
			html: true,
		},
		charMax: MAX_LENGTH_DESCRIPTION,
		showCharCount: true,
		required: true,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		placeholder: "Enter URL...",
		component: ReduxControls.Input,
		className: "full-width",
		tooltipVersion: 2,
		tip: {
			place: "right",
			description: `Redirects to different domains are not permitted.`,
		},
		charMax: MAX_LENGTH_LANDING_PAGE,
		showCharCount: true,
		required: true,
	},
	mobileLandingPage: {
		name: "mobileLandingPage",
		label: "Mobile Landing Page",
		placeholder: "Enter URL...",
		component: ReduxControls.Input,
		className: "full-width",
		tooltipVersion: 2,
		tip: {
			place: "right",
			description: `Redirects to different domains are not permitted.`,
		},
		charMax: MAX_LENGTH_LANDING_PAGE,
		showCharCount: true,
	},
	businessName: {
		name: "businessName",
		label: "Business Name",
		placeholder: "Enter Business Name...",
		component: ReduxControls.Input,
		className: "full-width",
		charMax: MAX_LENGTH_BUSINESS_NAME,
		showCharCount: true,
		required: true,
	},
	logo: {
		name: "logo",
		label: "Logo",
		prompt: "Select Image",
		showIcon: false,
		className: "full-width cypress-logo-select",
		helpTip: "1:1 aspect ratio. 1200x1200 and up recommended",
		assetType: ASSET_TYPES.IMAGE,
		component: AssetSelect,
		required: true,
		accept: IMAGE_ACCEPT,
	},
};

function fieldGenerator(props) {
	const {
		libraryId,
		navigationActions,
		showPristineErrors,
		isViewOnly,
	} = props;

	return (name) => {
		const fieldProps = GOOGLE_DEMAND_GEN_CAROUSEL_MAIN_FIELDS[name];

		let extraProps = {
			showPristineErrors,
			disabled: isViewOnly,
		};

		if(/^logo$/i.test(fieldProps.name)){
			extraProps.libraryId = libraryId;
			extraProps.navigationActions = navigationActions;
		}

		if(extraProps.disabled && fieldProps.placeholder){
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = "";
		}

		return (
			<Field
				key={fieldProps.name}
				{...fieldProps}
				{...extraProps}
			/>
		);
	};
}

export default function GoogleDemandGenMainContent(props){
	const {
		libraryId,
		adType,
		navigationActions,
		isViewOnly,
		showPristineErrors,
	} = props;

	const generateFields = fieldGenerator({
		libraryId,
		navigationActions,
		isViewOnly,
		showPristineErrors,
	});
	const fields = useMemo(() => (
		Object.keys(GOOGLE_DEMAND_GEN_CAROUSEL_MAIN_FIELDS).map(generateFields)
	), [adType]);

	return (
		<div className="form-container google-demand-gen-main-content">
			<div className="fields">
				{ fields }
			</div>
		</div>
	);
}

GoogleDemandGenMainContent.displayName = "GoogleDemandGenMainContent";
GoogleDemandGenMainContent.defaultProps = {
	isViewOnly: false,
	showPristineErrors: false,
};
